import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedTab, setSelectedTab] = useState('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // Ranking Analysis States
  const [rankingData, setRankingData] = useState(null);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [searchLocation, setSearchLocation] = useState('');
  const [searchSpecializations, setSearchSpecializations] = useState('');
  const [searchLimit, setSearchLimit] = useState(10);
  const [selectedRankingTherapist, setSelectedRankingTherapist] = useState(null);
  const [showRankingDetailModal, setShowRankingDetailModal] = useState(false);
  const [useDefaultSearch, setUseDefaultSearch] = useState(false);

  // Profile Search State
  const [profileSearch, setProfileSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Account Status Management States
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disableReason, setDisableReason] = useState('');

  // Backfill Coordinates State
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillResults, setBackfillResults] = useState(null);

  useEffect(() => {
    checkAuthentication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(profileSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [profileSearch]);

  useEffect(() => {
    if (isAuthenticated && selectedTab !== 'ranking') {
      fetchTherapists();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, currentPage, isAuthenticated, debouncedSearch]);

  const checkAuthentication = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('therapylist_token');
      
      if (!token) {
        navigate('/login', { replace: true });
        return;
      }

      // Verify token and check role by making a request to the admin endpoint
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${API_BASE_URL}/api/admin/dashboard/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        // Token is invalid or expired - clear everything and redirect
        localStorage.removeItem('token');
        localStorage.removeItem('therapylist_token');
        localStorage.removeItem('therapylist_user');
        
        // Check if it's a token expiration
        const errorData = await response.json().catch(() => ({}));
        if (errorData.message?.includes('expired')) {
          alert('Your session has expired for security. Please log in again.');
        }
        
        navigate('/login', { replace: true });
        return;
      }

      if (response.status === 403) {
        // User doesn't have superadmin privileges
        setError('Access denied. Super admin privileges required.');
        setAuthLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Authentication verification failed');
      }

      // User is authenticated and has proper role
      setIsAuthenticated(true);
    } catch (err) {
      console.error('Auth check failed:', err);
      localStorage.removeItem('token');
      localStorage.removeItem('therapylist_token');
      localStorage.removeItem('therapylist_user');
      navigate('/login', { replace: true });
    } finally {
      setAuthLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('therapylist_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${API_BASE_URL}/api/admin/dashboard/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('therapylist_token');
        localStorage.removeItem('therapylist_user');
        navigate('/login', { replace: true });
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const data = await response.json();
      setStats(data.data);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchTherapists = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('therapylist_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      
      let endpoint = `${API_BASE_URL}/api/admin/therapists/all`;
      if (selectedTab === 'pending') {
        endpoint = `${API_BASE_URL}/api/admin/therapists/pending`;
      }

      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        ...(selectedTab !== 'pending' && { status: selectedTab }),
        ...(debouncedSearch.trim() && { search: debouncedSearch.trim() })
      });

      const response = await fetch(`${endpoint}?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('therapylist_token');
        localStorage.removeItem('therapylist_user');
        navigate('/login', { replace: true });
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch therapists');
      }

      const data = await response.json();
      setTherapists(data.data);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (therapistId) => {
    try {
      setActionLoading(therapistId);
      const token = localStorage.getItem('token') || localStorage.getItem('therapylist_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      
      const response = await fetch(`${API_BASE_URL}/api/admin/therapists/${therapistId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ adminNotes })
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('therapylist_token');
        localStorage.removeItem('therapylist_user');
        navigate('/login', { replace: true });
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to approve therapist');
      }

      setSuccess('Therapist profile approved successfully');
      setAdminNotes('');
      fetchTherapists();
      fetchDashboardData();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setError('Rejection reason is required');
      return;
    }

    try {
      setActionLoading(selectedTherapist._id);
      const token = localStorage.getItem('token') || localStorage.getItem('therapylist_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      
      const response = await fetch(`${API_BASE_URL}/api/admin/therapists/${selectedTherapist._id}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          rejectionReason,
          adminNotes 
        })
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('therapylist_token');
        localStorage.removeItem('therapylist_user');
        navigate('/login', { replace: true });
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to reject therapist');
      }

      setSuccess('Therapist profile rejected');
      setShowRejectModal(false);
      setSelectedTherapist(null);
      setRejectionReason('');
      setAdminNotes('');
      fetchTherapists();
      fetchDashboardData();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('therapylist_token');
    localStorage.removeItem('therapylist_user');
    navigate('/login', { replace: true });
  };

  const handleDisableAccount = async () => {
    if (!disableReason.trim()) {
      setError('Disable reason is required');
      return;
    }

    try {
      setActionLoading(selectedTherapist._id);
      const token = localStorage.getItem('token') || localStorage.getItem('therapylist_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      
      const response = await fetch(`${API_BASE_URL}/api/admin/therapists/${selectedTherapist._id}/disable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          reason: disableReason
        })
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('therapylist_token');
        localStorage.removeItem('therapylist_user');
        navigate('/login', { replace: true });
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to disable therapist account');
      }

      setSuccess('Therapist account disabled successfully');
      setShowDisableModal(false);
      setSelectedTherapist(null);
      setDisableReason('');
      fetchTherapists();
      fetchDashboardData();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleEnableAccount = async (therapistId) => {
    try {
      setActionLoading(therapistId);
      const token = localStorage.getItem('token') || localStorage.getItem('therapylist_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      
      const response = await fetch(`${API_BASE_URL}/api/admin/therapists/${therapistId}/enable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('therapylist_token');
        localStorage.removeItem('therapylist_user');
        navigate('/login', { replace: true });
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to enable therapist account');
      }

      setSuccess('Therapist account enabled successfully');
      fetchTherapists();
      fetchDashboardData();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleBackfillCoordinates = async () => {
    try {
      setBackfillLoading(true);
      setError('');
      setBackfillResults(null);
      const token = localStorage.getItem('token') || localStorage.getItem('therapylist_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      const response = await fetch(`${API_BASE_URL}/api/admin/therapists/backfill-coordinates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('therapylist_token');
        localStorage.removeItem('therapylist_user');
        navigate('/login', { replace: true });
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to backfill coordinates');
      }

      const data = await response.json();
      setBackfillResults(data.results);
      setSuccess(data.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setBackfillLoading(false);
    }
  };

  const fetchRankingExplanation = async () => {
    if (!useDefaultSearch && !searchLocation.trim()) {
      setError('Please enter a location to analyze rankings or check "Use Default Search"');
      return;
    }

    try {
      setRankingLoading(true);
      setError('');
      const token = localStorage.getItem('token') || localStorage.getItem('therapylist_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      
      const params = new URLSearchParams({
        limit: searchLimit
      });

      // Only add location if not using default search and location is provided
      if (!useDefaultSearch && searchLocation.trim()) {
        params.append('location', searchLocation);
      }

      if (searchSpecializations.trim()) {
        params.append('specializations', searchSpecializations);
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/ranking/explain?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('therapylist_token');
        localStorage.removeItem('therapylist_user');
        navigate('/login', { replace: true });
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch ranking explanation');
      }

      const data = await response.json();
      setRankingData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setRankingLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
      approved: 'bg-green-50 text-green-700 border border-green-200',
      rejected: 'bg-red-50 text-red-700 border border-red-200'
    };

    return (
      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${statusConfig[status]}`} style={{fontFamily: 'Nunito Sans, sans-serif'}}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getAccountStatusBadge = (isActive) => {
    if (isActive === false) {
      return (
        <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-red-50 text-red-700 border border-red-200" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
          Disabled
        </span>
      );
    }
    return (
      <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-green-50 text-green-700 border border-green-200" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
        Active
      </span>
    );
  };

  // Show loading screen while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#e4fde1] to-white flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-[#254e70]/10 p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#254e70] mx-auto mb-6"></div>
          <h1 className="text-2xl font-medium text-[#254e70] mb-3" style={{fontFamily: 'Kanit, sans-serif'}}>Verifying Access</h1>
          <p className="text-gray-600" style={{fontFamily: 'Nunito Sans, sans-serif'}}>Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Show access denied if not authenticated or insufficient privileges
  if (!isAuthenticated || (error && error.includes('Access denied'))) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#e4fde1] to-white flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-[#254e70]/10 p-8 text-center">
          <div className="text-red-500 text-6xl mb-6">⛔</div>
          <h1 className="text-3xl font-medium text-[#254e70] mb-3" style={{fontFamily: 'Kanit, sans-serif'}}>Access Denied</h1>
          <p className="text-gray-600 mb-8" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
            {error && error.includes('Access denied') 
              ? 'You don\'t have super admin privileges.' 
              : 'You must be logged in to access this page.'
            }
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-[#254e70] text-white py-3 px-6 rounded-xl hover:bg-[#254e70]/90 transition-all duration-200 font-medium"
              style={{fontFamily: 'Nunito Sans, sans-serif'}}
            >
              Go to Login
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-[#e4fde1] text-[#254e70] py-3 px-6 rounded-xl hover:bg-[#e4fde1]/80 transition-all duration-200 font-medium border border-[#254e70]/20"
              style={{fontFamily: 'Nunito Sans, sans-serif'}}
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e4fde1] to-white">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-[#254e70]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-medium text-[#254e70] mb-1" style={{fontFamily: 'Kanit, sans-serif'}}>Super Admin Dashboard</h1>
              <p className="text-gray-600" style={{fontFamily: 'Nunito Sans, sans-serif'}}>Manage therapist profile approvals</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="text-gray-500 hover:text-[#254e70] transition-colors duration-200 font-medium"
                style={{fontFamily: 'Nunito Sans, sans-serif'}}
              >
                ← Back to Home
              </button>
              <button
                onClick={handleLogout}
                className="bg-red-500 text-white px-6 py-2 rounded-xl hover:bg-red-600 transition-all duration-200 font-medium"
                style={{fontFamily: 'Nunito Sans, sans-serif'}}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-6 py-4 rounded-xl" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
            {success}
            <button 
              onClick={() => setSuccess('')}
              className="float-right text-green-700 hover:text-green-900 font-bold text-lg"
            >
              ×
            </button>
          </div>
        )}

        {error && !error.includes('Access denied') && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
            {error}
            <button 
              onClick={() => setError('')}
              className="float-right text-red-700 hover:text-red-900 font-bold text-lg"
            >
              ×
            </button>
          </div>
        )}

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white overflow-hidden shadow-lg rounded-2xl border border-[#254e70]/10">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl flex items-center justify-center shadow-sm">
                      <span className="text-white font-bold text-lg">⏳</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                        Pending Approval
                      </dt>
                      <dd className="text-3xl font-medium text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                        {stats.profiles.pending}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow-lg rounded-2xl border border-[#254e70]/10">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-500 rounded-xl flex items-center justify-center shadow-sm">
                      <span className="text-white font-bold text-lg">✓</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                        Approved
                      </dt>
                      <dd className="text-3xl font-medium text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                        {stats.profiles.approved}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow-lg rounded-2xl border border-[#254e70]/10">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-red-500 rounded-xl flex items-center justify-center shadow-sm">
                      <span className="text-white font-bold text-lg">✗</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                        Rejected
                      </dt>
                      <dd className="text-3xl font-medium text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                        {stats.profiles.rejected}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow-lg rounded-2xl border border-[#254e70]/10">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#9dacff] to-[#8a99ff] rounded-xl flex items-center justify-center shadow-sm">
                      <span className="text-white font-bold text-lg">👥</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                        Total Users
                      </dt>
                      <dd className="text-3xl font-medium text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                        {stats.users.total}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white shadow-lg rounded-2xl border border-[#254e70]/10">
          <div className="border-b border-[#254e70]/10">
            <nav className="-mb-px flex space-x-8 px-6">
              {['pending', 'approved', 'rejected', 'ranking'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setSelectedTab(tab);
                    setCurrentPage(1);
                    setProfileSearch('');
                    setDebouncedSearch('');
                  }}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-all duration-200 ${
                    selectedTab === tab
                      ? 'border-[#254e70] text-[#254e70]'
                      : 'border-transparent text-gray-500 hover:text-[#254e70] hover:border-[#254e70]/30'
                  }`}
                  style={{fontFamily: 'Nunito Sans, sans-serif'}}
                >
                  {tab === 'ranking' ? 'Ranking Analysis' : `${tab.charAt(0).toUpperCase() + tab.slice(1)} Profiles`}
                </button>
              ))}
            </nav>
          </div>

          {/* Ranking Analysis Content */}
          {selectedTab === 'ranking' ? (
            <div className="p-6">
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-[#254e70] mb-6" style={{fontFamily: 'Kanit, sans-serif'}}>
                  Ranking Analysis Tool
                </h3>
                <p className="text-gray-600 mb-4" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                  Analyze how therapists rank for specific searches and understand the scoring breakdown.
                </p>
                <div className="bg-blue-50 rounded-xl p-4 mb-6 border-l-4 border-blue-400">
                  <h4 className="font-semibold text-blue-800 mb-2" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                    🔄 Algorithm Updated - Anti-Feedback Loop Design
                  </h4>
                  <p className="text-sm text-blue-700" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                    <strong>New weights:</strong> Profile Quality 40% (↑), Proximity 35%, Engagement 15% (↓), Randomness 10% (new). 
                    This prevents the "rich get richer" problem where top-ranked therapists stay at the top due to higher interaction rates.
                  </p>
                </div>
                
                {/* Search Form */}
                <div className="bg-[#e4fde1]/20 rounded-xl p-6 mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#254e70] mb-2" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                        Location {!useDefaultSearch ? '*' : '(Optional when using Default Search)'}
                      </label>
                      <input
                        type="text"
                        value={searchLocation}
                        onChange={(e) => setSearchLocation(e.target.value)}
                        placeholder="e.g., Louisville, KY or 40202"
                        disabled={useDefaultSearch}
                        className={`w-full px-4 py-3 border border-[#254e70]/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#9dacff] focus:border-transparent transition-all duration-200 ${useDefaultSearch ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        style={{fontFamily: 'Nunito Sans, sans-serif'}}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#254e70] mb-2" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                        Specializations (Optional)
                      </label>
                      <input
                        type="text"
                        value={searchSpecializations}
                        onChange={(e) => setSearchSpecializations(e.target.value)}
                        placeholder="e.g., anxiety, depression"
                        className="w-full px-4 py-3 border border-[#254e70]/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#9dacff] focus:border-transparent transition-all duration-200"
                        style={{fontFamily: 'Nunito Sans, sans-serif'}}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#254e70] mb-2" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                        Results Limit
                      </label>
                      <select
                        value={searchLimit}
                        onChange={(e) => setSearchLimit(parseInt(e.target.value))}
                        className="w-full px-4 py-3 border border-[#254e70]/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#9dacff] focus:border-transparent transition-all duration-200"
                        style={{fontFamily: 'Nunito Sans, sans-serif'}}
                      >
                        <option value={5}>5 therapists</option>
                        <option value={10}>10 therapists</option>
                        <option value={15}>15 therapists</option>
                        <option value={20}>20 therapists</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center mb-4">
                    <input
                      type="checkbox"
                      id="useDefaultSearch"
                      checked={useDefaultSearch}
                      onChange={(e) => setUseDefaultSearch(e.target.checked)}
                      className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#9dacff] border-gray-300 rounded"
                      style={{fontFamily: 'Nunito Sans, sans-serif'}}
                    />
                    <label htmlFor="useDefaultSearch" className="text-sm text-gray-700" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                      Use Default Search (Browse all therapists)
                    </label>
                  </div>
                  <button
                    onClick={fetchRankingExplanation}
                    disabled={rankingLoading || (!searchLocation.trim() && !useDefaultSearch)}
                    className="bg-[#254e70] text-white px-6 py-3 rounded-xl hover:bg-[#1a3a52] disabled:opacity-50 transition-all duration-200 font-medium shadow-sm"
                    style={{fontFamily: 'Nunito Sans, sans-serif'}}
                  >
                    {rankingLoading ? 'Analyzing...' : 'Analyze Rankings'}
                  </button>
                </div>

                {/* Ranking Results */}
                {rankingLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#254e70] mx-auto"></div>
                    <p className="mt-6 text-gray-600" style={{fontFamily: 'Nunito Sans, sans-serif'}}>Analyzing rankings...</p>
                  </div>
                ) : rankingData ? (
                  <div className="space-y-6">
                    {/* Search Info */}
                    <div className="bg-blue-50 rounded-xl p-4">
                      <h4 className="font-semibold text-[#254e70] mb-2" style={{fontFamily: 'Kanit, sans-serif'}}>Search Criteria</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                        <div>
                          <span className="font-medium">Location:</span> {useDefaultSearch ? 'All Therapists' : searchLocation}
                        </div>
                        <div>
                          <span className="font-medium">Specializations:</span> {searchSpecializations || 'All'}
                        </div>
                        <div>
                          <span className="font-medium">Total Analyzed:</span> {rankingData.totalAnalyzed}
                        </div>
                      </div>
                    </div>

                    {/* Algorithm Weights */}
                    <div className="bg-purple-50 rounded-xl p-4">
                      <h4 className="font-semibold text-[#254e70] mb-2" style={{fontFamily: 'Kanit, sans-serif'}}>Current Algorithm Weights (Anti-Feedback Loop Design)</h4>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                        <div>
                          <span className="font-medium">Proximity:</span> {Math.round(rankingData.algorithmWeights.categoryWeights.PROXIMITY * 100)}%
                        </div>
                        <div>
                          <span className="font-medium">Profile Quality:</span> {Math.round(rankingData.algorithmWeights.categoryWeights.PROFILE_QUALITY * 100)}%
                        </div>
                        <div>
                          <span className="font-medium">Engagement:</span> {Math.round(rankingData.algorithmWeights.categoryWeights.ENGAGEMENT * 100)}%
                          <span className="text-xs text-gray-600 block">(Reduced to prevent feedback loop)</span>
                        </div>
                        <div>
                          <span className="font-medium">Randomness:</span> {Math.round((rankingData.algorithmWeights.categoryWeights.RANDOMNESS || 0) * 100)}%
                          <span className="text-xs text-gray-600 block">(Prevents stagnation)</span>
                        </div>
                      </div>
                    </div>

                    {/* Rankings Table */}
                    <div className="bg-white border border-[#254e70]/10 rounded-xl overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-[#254e70]/10">
                          <thead className="bg-[#e4fde1]/30">
                            <tr>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-[#254e70] uppercase tracking-wider" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                                Rank
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-[#254e70] uppercase tracking-wider" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                                Therapist
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-[#254e70] uppercase tracking-wider" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                                Total Score
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-[#254e70] uppercase tracking-wider" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                                Accepting Clients
                              </th>
                                                             <th className="px-6 py-4 text-left text-xs font-semibold text-[#254e70] uppercase tracking-wider" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                                 Category Scores
                               </th>
                               <th className="px-6 py-4 text-left text-xs font-semibold text-[#254e70] uppercase tracking-wider" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                                 Actions
                               </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-[#254e70]/5">
                            {rankingData.rankings.map((therapist) => (
                              <tr key={therapist.therapistInfo.id} className="hover:bg-[#e4fde1]/20 transition-colors duration-150">
                                                                 <td className="px-6 py-4 whitespace-nowrap">
                                   <div className="flex items-center justify-center w-8 h-8 bg-[#254e70] text-white rounded-full font-bold text-sm">
                                     {therapist.rank || 0}
                                   </div>
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap">
                                   <div>
                                     <div className="text-sm font-semibold text-[#254e70]" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                                       {therapist.therapistInfo?.name || 'Unknown'}
                                     </div>
                                     <div className="text-sm text-gray-500" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                                       {therapist.therapistInfo?.city || 'N/A'}, {therapist.therapistInfo?.state || 'N/A'}
                                     </div>
                                   </div>
                                 </td>
                                                                 <td className="px-6 py-4 whitespace-nowrap">
                                   <div className="text-lg font-bold text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                                     {(therapist.totalScore || 0).toFixed(1)}
                                   </div>
                                   {therapist.baseScore !== therapist.totalScore && (
                                     <div className="text-xs text-gray-500" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                                       Base: {(therapist.baseScore || 0).toFixed(1)}
                                     </div>
                                   )}
                                 </td>
                                                                 <td className="px-6 py-4 whitespace-nowrap">
                                   <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                                     therapist.acceptingNewClients !== false
                                       ? 'bg-green-50 text-green-700 border border-green-200'
                                       : 'bg-red-50 text-red-700 border border-red-200'
                                   }`} style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                                     {therapist.acceptingNewClients !== false ? 'Yes (1.0x)' : 'No (0.25x)'}
                                   </span>
                                 </td>
                                <td className="px-6 py-4">
                                                                     <div className="space-y-1 text-xs" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                                     <div className="flex justify-between">
                                       <span>Proximity:</span>
                                       <span className="font-medium">{(therapist.categoryBreakdown?.proximity?.weightedScore || 0).toFixed(1)}</span>
                                     </div>
                                     <div className="flex justify-between">
                                       <span>Profile Quality:</span>
                                       <span className="font-medium">{(therapist.categoryBreakdown?.profileQuality?.weightedScore || 0).toFixed(1)}</span>
                                     </div>
                                     <div className="flex justify-between">
                                       <span>Engagement:</span>
                                       <span className="font-medium">{(therapist.categoryBreakdown?.engagement?.weightedScore || 0).toFixed(1)}</span>
                                     </div>
                                     <div className="flex justify-between">
                                       <span>Randomness:</span>
                                       <span className="font-medium text-purple-600">{(therapist.categoryBreakdown?.randomness?.weightedScore || 0).toFixed(1)}</span>
                                     </div>
                                   </div>
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap">
                                   <button
                                     onClick={() => {
                                       setSelectedRankingTherapist(therapist);
                                       setShowRankingDetailModal(true);
                                     }}
                                     className="bg-[#254e70] text-white px-3 py-2 rounded-lg text-xs hover:bg-[#1a3a52] transition-all duration-200 font-medium shadow-sm"
                                     style={{fontFamily: 'Nunito Sans, sans-serif'}}
                                   >
                                     View Details
                                   </button>
                                 </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-[#254e70] text-6xl mb-6">📊</div>
                    <p className="text-gray-600 text-lg" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                      Enter search criteria above or use Default Search to analyze therapist rankings
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Therapists Table for other tabs */
            <div className="p-6">
              {/* Search Bar */}
              <div className="mb-6">
                <div className="relative">
                  <input
                    type="text"
                    value={profileSearch}
                    onChange={(e) => {
                      setProfileSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    placeholder="Search by name, email, city, state, or practice name..."
                    className="w-full px-4 py-3 pl-11 border border-[#254e70]/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#9dacff] focus:border-transparent transition-all duration-200"
                    style={{fontFamily: 'Nunito Sans, sans-serif'}}
                  />
                  <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {profileSearch && (
                    <button
                      onClick={() => {
                        setProfileSearch('');
                        setCurrentPage(1);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#254e70] mx-auto"></div>
                  <p className="mt-6 text-gray-600" style={{fontFamily: 'Nunito Sans, sans-serif'}}>Loading therapists...</p>
                </div>
              ) : therapists.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600 text-lg" style={{fontFamily: 'Nunito Sans, sans-serif'}}>No {selectedTab} profiles found.</p>
                </div>
              ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-[#254e70]/10">
                    <thead className="bg-[#e4fde1]/30">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-[#254e70] uppercase tracking-wider" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                          Therapist
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-[#254e70] uppercase tracking-wider" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                          Email
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-[#254e70] uppercase tracking-wider" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                          Location
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-[#254e70] uppercase tracking-wider" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                          Approval Status
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-[#254e70] uppercase tracking-wider" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                          Account Status
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-[#254e70] uppercase tracking-wider" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                          Submitted
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-[#254e70] uppercase tracking-wider" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-[#254e70]/5">
                      {therapists.map((therapist) => (
                        <tr key={therapist._id} className="hover:bg-[#e4fde1]/20 transition-colors duration-150">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#254e70] to-[#1a3a52] flex items-center justify-center shadow-sm">
                                <span className="text-sm font-medium text-white" style={{fontFamily: 'Kanit, sans-serif'}}>
                                  {therapist.firstName?.[0]}{therapist.lastName?.[0]}
                                </span>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-semibold text-[#254e70]" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                                  {therapist.firstName} {therapist.lastName}
                                </div>
                                <div className="text-sm text-gray-500" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                                  {therapist.title}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-[#254e70]" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                            {therapist.userId?.email || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-[#254e70]" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                            {therapist.location?.city}, {therapist.location?.state}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(therapist.approval?.status || 'pending')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getAccountStatusBadge(therapist.isActive)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                            {formatDate(therapist.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2 flex-wrap">
                              <button
                                onClick={() => {
                                  setSelectedTherapist(therapist);
                                  setShowDetailModal(true);
                                }}
                                className="bg-[#254e70] text-white px-3 py-2 rounded-lg text-xs hover:bg-[#1a3a52] transition-all duration-200 font-medium shadow-sm mb-1"
                                style={{fontFamily: 'Nunito Sans, sans-serif'}}
                              >
                                View Details
                              </button>
                              
                              {/* Account Status Actions */}
                              {therapist.isActive === false ? (
                                <button
                                  onClick={() => handleEnableAccount(therapist._id)}
                                  disabled={actionLoading === therapist._id}
                                  className="bg-blue-500 text-white px-3 py-2 rounded-lg text-xs hover:bg-blue-600 disabled:opacity-50 transition-all duration-200 font-medium shadow-sm mb-1"
                                  style={{fontFamily: 'Nunito Sans, sans-serif'}}
                                >
                                  {actionLoading === therapist._id ? 'Enabling...' : 'Enable'}
                                </button>
                              ) : (
                                <button
                                  onClick={() => {
                                    setSelectedTherapist(therapist);
                                    setShowDisableModal(true);
                                  }}
                                  disabled={actionLoading === therapist._id}
                                  className="bg-orange-500 text-white px-3 py-2 rounded-lg text-xs hover:bg-orange-600 disabled:opacity-50 transition-all duration-200 font-medium shadow-sm mb-1"
                                  style={{fontFamily: 'Nunito Sans, sans-serif'}}
                                >
                                  Disable
                                </button>
                              )}

                              {/* Approval Actions */}
                              {selectedTab === 'pending' && (
                                <>
                                  <button
                                    onClick={() => handleApprove(therapist._id)}
                                    disabled={actionLoading === therapist._id}
                                    className="bg-green-500 text-white px-3 py-2 rounded-lg text-xs hover:bg-green-600 disabled:opacity-50 transition-all duration-200 font-medium shadow-sm mb-1"
                                    style={{fontFamily: 'Nunito Sans, sans-serif'}}
                                  >
                                    {actionLoading === therapist._id ? 'Approving...' : 'Approve'}
                                  </button>
                                  <button
                                    onClick={() => {
                                      setSelectedTherapist(therapist);
                                      setShowRejectModal(true);
                                    }}
                                    disabled={actionLoading === therapist._id}
                                    className="bg-red-500 text-white px-3 py-2 rounded-lg text-xs hover:bg-red-600 disabled:opacity-50 transition-all duration-200 font-medium shadow-sm mb-1"
                                    style={{fontFamily: 'Nunito Sans, sans-serif'}}
                                >
                                  Reject
                                </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8 flex justify-between items-center">
                    <div className="text-sm text-gray-700" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                      Page {currentPage} of {totalPages}
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-4 py-2 border border-[#254e70]/20 rounded-xl text-sm disabled:opacity-50 hover:bg-[#e4fde1]/30 transition-all duration-200 font-medium"
                        style={{fontFamily: 'Nunito Sans, sans-serif'}}
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 border border-[#254e70]/20 rounded-xl text-sm disabled:opacity-50 hover:bg-[#e4fde1]/30 transition-all duration-200 font-medium"
                        style={{fontFamily: 'Nunito Sans, sans-serif'}}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
            </div>
          )}
        </div>

        {/* Admin Tools */}
        <div className="bg-white shadow-lg rounded-2xl border border-[#254e70]/10 mt-8 p-6">
          <h3 className="text-lg font-medium text-[#254e70] mb-4" style={{fontFamily: 'Kanit, sans-serif'}}>
            Admin Tools
          </h3>
          <div className="flex flex-wrap items-start gap-4">
            <div>
              <button
                onClick={handleBackfillCoordinates}
                disabled={backfillLoading}
                className="bg-[#254e70] text-white px-5 py-2.5 rounded-xl hover:bg-[#254e70]/90 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                style={{fontFamily: 'Nunito Sans, sans-serif'}}
              >
                {backfillLoading ? 'Backfilling...' : 'Backfill Missing Coordinates'}
              </button>
              <p className="text-xs text-gray-500 mt-1.5 max-w-xs" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                Geocodes therapists missing location coordinates so they appear in location searches.
              </p>
            </div>
            {backfillResults && (
              <div className="bg-[#e4fde1]/30 rounded-xl p-4 text-sm flex-1 min-w-[250px]" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                <p className="font-semibold text-[#254e70] mb-2">Backfill Results</p>
                <div className="space-y-1 text-[#254e70]/80">
                  <p><span className="font-medium text-green-700">{backfillResults.updated.length}</span> updated</p>
                  <p><span className="font-medium text-red-600">{backfillResults.failed.length}</span> failed (city not in geocoding database)</p>
                  <p><span className="font-medium text-gray-500">{backfillResults.skipped.length}</span> skipped (no location data)</p>
                </div>
                {backfillResults.updated.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-[#254e70] font-medium text-xs">Show updated therapists</summary>
                    <ul className="mt-1 space-y-0.5 text-xs text-[#254e70]/70">
                      {backfillResults.updated.map((t) => (
                        <li key={t.id}>{t.name} — {t.city}, {t.state}</li>
                      ))}
                    </ul>
                  </details>
                )}
                {backfillResults.failed.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-red-600 font-medium text-xs">Show failed therapists</summary>
                    <ul className="mt-1 space-y-0.5 text-xs text-red-600/70">
                      {backfillResults.failed.map((t) => (
                        <li key={t.id}>{t.name} — {t.city}, {t.state} ({t.zipCode || 'no zip'})</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl border border-[#254e70]/10 w-full max-w-lg">
            <div className="p-8">
              <h3 className="text-2xl font-medium text-[#254e70] mb-6" style={{fontFamily: 'Kanit, sans-serif'}}>
                Reject Profile: {selectedTherapist?.firstName} {selectedTherapist?.lastName}
              </h3>
              <div className="mb-6">
                <label className="block text-sm font-semibold text-[#254e70] mb-3" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                  Rejection Reason *
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full px-4 py-3 border border-[#254e70]/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#9dacff] focus:border-transparent transition-all duration-200"
                  style={{fontFamily: 'Nunito Sans, sans-serif'}}
                  rows="4"
                  placeholder="Please provide a reason for rejection..."
                />
              </div>
              <div className="mb-8">
                <label className="block text-sm font-semibold text-[#254e70] mb-3" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                  Admin Notes (Optional)
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="w-full px-4 py-3 border border-[#254e70]/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#9dacff] focus:border-transparent transition-all duration-200"
                  style={{fontFamily: 'Nunito Sans, sans-serif'}}
                  rows="3"
                  placeholder="Internal notes..."
                />
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedTherapist(null);
                    setRejectionReason('');
                    setAdminNotes('');
                  }}
                  className="px-6 py-3 bg-[#e4fde1] text-[#254e70] rounded-xl hover:bg-[#e4fde1]/80 transition-all duration-200 font-medium border border-[#254e70]/20"
                  style={{fontFamily: 'Nunito Sans, sans-serif'}}
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={!rejectionReason.trim() || actionLoading}
                  className="px-6 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 disabled:opacity-50 transition-all duration-200 font-medium shadow-sm"
                  style={{fontFamily: 'Nunito Sans, sans-serif'}}
                >
                  {actionLoading ? 'Rejecting...' : 'Reject Profile'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disable Account Modal */}
      {showDisableModal && (
        <div className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl border border-[#254e70]/10 w-full max-w-lg">
            <div className="p-8">
              <h3 className="text-2xl font-medium text-[#254e70] mb-6" style={{fontFamily: 'Kanit, sans-serif'}}>
                Disable Account: {selectedTherapist?.firstName} {selectedTherapist?.lastName}
              </h3>
              <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-xl">
                <p className="text-sm text-orange-700" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                  <strong>Note:</strong> Disabling this account will remove it from search results and prevent clients from finding this therapist. The therapist will still be able to log in and update their profile.
                </p>
              </div>
              <div className="mb-8">
                <label className="block text-sm font-semibold text-[#254e70] mb-3" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                  Reason for Disabling *
                </label>
                <textarea
                  value={disableReason}
                  onChange={(e) => setDisableReason(e.target.value)}
                  className="w-full px-4 py-3 border border-[#254e70]/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#9dacff] focus:border-transparent transition-all duration-200"
                  style={{fontFamily: 'Nunito Sans, sans-serif'}}
                  rows="4"
                  placeholder="Please provide a reason for disabling this account..."
                />
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => {
                    setShowDisableModal(false);
                    setSelectedTherapist(null);
                    setDisableReason('');
                  }}
                  className="px-6 py-3 bg-[#e4fde1] text-[#254e70] rounded-xl hover:bg-[#e4fde1]/80 transition-all duration-200 font-medium border border-[#254e70]/20"
                  style={{fontFamily: 'Nunito Sans, sans-serif'}}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDisableAccount}
                  disabled={!disableReason.trim() || actionLoading}
                  className="px-6 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 disabled:opacity-50 transition-all duration-200 font-medium shadow-sm"
                  style={{fontFamily: 'Nunito Sans, sans-serif'}}
                >
                  {actionLoading ? 'Disabling...' : 'Disable Account'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Therapist Detail Modal */}
      {showDetailModal && selectedTherapist && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-8 border w-full max-w-4xl shadow-2xl rounded-2xl bg-white my-10">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                {selectedTherapist.firstName} {selectedTherapist.lastName}
              </h2>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedTherapist(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="max-h-[80vh] overflow-y-auto space-y-8">
              {/* Basic Information */}
              <div className="bg-[#e4fde1]/20 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-[#254e70] mb-4" style={{fontFamily: 'Kanit, sans-serif'}}>
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#254e70] mb-1">Name</label>
                    <p className="text-gray-900">{selectedTherapist.firstName} {selectedTherapist.lastName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#254e70] mb-1">Title</label>
                    <p className="text-gray-900">{selectedTherapist.title || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#254e70] mb-1">Email</label>
                    <p className="text-gray-900">{selectedTherapist.email || selectedTherapist.userId?.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#254e70] mb-1">Phone</label>
                    <p className="text-gray-900">{selectedTherapist.phone || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#254e70] mb-1">Website</label>
                    <p className="text-gray-900">{selectedTherapist.website || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#254e70] mb-1">Practice Name</label>
                    <p className="text-gray-900">{selectedTherapist.practiceName || 'Not specified'}</p>
                  </div>
                </div>
              </div>

              {/* Location */}
              {selectedTherapist.location && (
                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-[#254e70] mb-4" style={{fontFamily: 'Kanit, sans-serif'}}>
                    Location & Service Area
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#254e70] mb-1">Address</label>
                      <p className="text-gray-900">{selectedTherapist.location.address || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#254e70] mb-1">City, State</label>
                      <p className="text-gray-900">
                        {selectedTherapist.location.city || 'Not specified'}, {selectedTherapist.location.state || 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#254e70] mb-1">ZIP Code</label>
                      <p className="text-gray-900">{selectedTherapist.location.zipCode || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#254e70] mb-1">Service Radius</label>
                      <p className="text-gray-900">{selectedTherapist.location.serviceRadius || 50} miles</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Professional Information */}
              {selectedTherapist.professional && (
                <div className="bg-yellow-50 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-[#254e70] mb-4" style={{fontFamily: 'Kanit, sans-serif'}}>
                    Professional Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#254e70] mb-1">Professional Licenses</label>
                      {selectedTherapist.professional.licenses && selectedTherapist.professional.licenses.length > 0 ? (
                        <div className="space-y-2">
                          {selectedTherapist.professional.licenses.map((license, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                              <div>
                                <span className="font-medium">{license.number}</span>
                                {license.state && <span className="ml-2 text-gray-600">({license.state})</span>}
                                <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                                  license.status === 'active' ? 'bg-green-100 text-green-800' :
                                  license.status === 'expired' ? 'bg-red-100 text-red-800' :
                                  license.status === 'suspended' ? 'bg-orange-100 text-orange-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {license.status || 'Active'}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div>
                          <p className="text-gray-900">
                            {selectedTherapist.professional.licenseNumber || 'No license information'}
                            {selectedTherapist.professional.licenseNumber && ' (Legacy format)'}
                          </p>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#254e70] mb-1">Years of Experience</label>
                      <p className="text-gray-900">{selectedTherapist.professional.yearsExperience || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#254e70] mb-1">Languages</label>
                      <p className="text-gray-900">
                        {selectedTherapist.professional.languages?.length > 0 
                          ? selectedTherapist.professional.languages.join(', ') 
                          : 'Not specified'}
                      </p>
                    </div>
                  </div>
                  
                  {selectedTherapist.professional.education?.length > 0 && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-[#254e70] mb-2">Education</label>
                      <div className="space-y-2">
                        {selectedTherapist.professional.education.map((edu, index) => (
                          <div key={index} className="bg-white p-3 rounded-lg">
                            <p className="font-medium">{edu.degree} - {edu.fieldOfStudy}</p>
                            <p className="text-sm text-gray-600">{edu.institution} ({edu.graduationYear})</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Specializations */}
              <div className="bg-purple-50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-[#254e70] mb-4" style={{fontFamily: 'Kanit, sans-serif'}}>
                  Practice Details
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#254e70] mb-2">Specializations</label>
                    <p className="text-gray-900">
                      {selectedTherapist.specializations?.length > 0 
                        ? selectedTherapist.specializations.join(', ') 
                        : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#254e70] mb-2">Treatment Approaches</label>
                    <p className="text-gray-900">
                      {selectedTherapist.treatmentApproaches?.length > 0 
                        ? selectedTherapist.treatmentApproaches.join(', ') 
                        : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#254e70] mb-2">Age Groups</label>
                    <p className="text-gray-900">
                      {selectedTherapist.ageGroups?.length > 0 
                        ? selectedTherapist.ageGroups.join(', ') 
                        : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#254e70] mb-2">Session Types</label>
                    <p className="text-gray-900">
                      {selectedTherapist.sessionTypes?.length > 0 
                        ? selectedTherapist.sessionTypes.join(', ') 
                        : 'Not specified'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Bio */}
              {selectedTherapist.bio && (
                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-[#254e70] mb-4" style={{fontFamily: 'Kanit, sans-serif'}}>
                    Bio
                  </h3>
                  <p className="text-gray-900 leading-relaxed">{selectedTherapist.bio}</p>
                </div>
              )}

              {/* Account Status */}
              <div className="bg-blue-50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-[#254e70] mb-4" style={{fontFamily: 'Kanit, sans-serif'}}>
                  Account Status
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#254e70] mb-1">Current Status</label>
                    {getAccountStatusBadge(selectedTherapist.isActive)}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#254e70] mb-1">Accepting New Clients</label>
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                      selectedTherapist.acceptingNewClients
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-gray-50 text-gray-700 border border-gray-200'
                    }`} style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                      {selectedTherapist.acceptingNewClients ? 'Yes' : 'No'}
                    </span>
                  </div>
                  {selectedTherapist.disabledAt && (
                    <div>
                      <label className="block text-sm font-medium text-[#254e70] mb-1">Disabled At</label>
                      <p className="text-gray-900">{formatDate(selectedTherapist.disabledAt)}</p>
                    </div>
                  )}
                  {selectedTherapist.enabledAt && (
                    <div>
                      <label className="block text-sm font-medium text-[#254e70] mb-1">Last Enabled At</label>
                      <p className="text-gray-900">{formatDate(selectedTherapist.enabledAt)}</p>
                    </div>
                  )}
                  {selectedTherapist.disableReason && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-[#254e70] mb-1">Disable Reason</label>
                      <p className="text-gray-900">{selectedTherapist.disableReason}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Approval Status */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-[#254e70] mb-4" style={{fontFamily: 'Kanit, sans-serif'}}>
                  Approval Status
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#254e70] mb-1">Current Status</label>
                    {getStatusBadge(selectedTherapist.approval?.status || 'pending')}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#254e70] mb-1">Submitted</label>
                    <p className="text-gray-900">{formatDate(selectedTherapist.createdAt)}</p>
                  </div>
                  {selectedTherapist.approval?.approvedAt && (
                    <div>
                      <label className="block text-sm font-medium text-[#254e70] mb-1">Approved At</label>
                      <p className="text-gray-900">{formatDate(selectedTherapist.approval.approvedAt)}</p>
                    </div>
                  )}
                  {selectedTherapist.approval?.rejectedAt && (
                    <div>
                      <label className="block text-sm font-medium text-[#254e70] mb-1">Rejected At</label>
                      <p className="text-gray-900">{formatDate(selectedTherapist.approval.rejectedAt)}</p>
                    </div>
                  )}
                  {selectedTherapist.approval?.rejectionReason && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-[#254e70] mb-1">Rejection Reason</label>
                      <p className="text-gray-900">{selectedTherapist.approval.rejectionReason}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex justify-between items-center pt-6 border-t border-gray-200">
              {/* Account Status Actions */}
              <div className="flex space-x-4">
                {selectedTherapist.isActive === false ? (
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      handleEnableAccount(selectedTherapist._id);
                    }}
                    disabled={actionLoading === selectedTherapist._id}
                    className="px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 transition-all duration-200 font-medium shadow-sm"
                    style={{fontFamily: 'Nunito Sans, sans-serif'}}
                  >
                    {actionLoading === selectedTherapist._id ? 'Enabling...' : 'Enable Account'}
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setSelectedTherapist(selectedTherapist);
                      setShowDisableModal(true);
                    }}
                    className="px-6 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-all duration-200 font-medium shadow-sm"
                    style={{fontFamily: 'Nunito Sans, sans-serif'}}
                  >
                    Disable Account
                  </button>
                )}
              </div>
              
              {/* Approval Actions */}
              {selectedTherapist.approval?.status === 'pending' && (
                <div className="flex space-x-4">
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setSelectedTherapist(selectedTherapist);
                      setShowRejectModal(true);
                    }}
                    className="px-6 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all duration-200 font-medium shadow-sm"
                    style={{fontFamily: 'Nunito Sans, sans-serif'}}
                  >
                    Reject Profile
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      handleApprove(selectedTherapist._id);
                    }}
                    disabled={actionLoading === selectedTherapist._id}
                    className="px-6 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 disabled:opacity-50 transition-all duration-200 font-medium shadow-sm"
                    style={{fontFamily: 'Nunito Sans, sans-serif'}}
                  >
                    {actionLoading === selectedTherapist._id ? 'Approving...' : 'Approve Profile'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ranking Detail Modal */}
      {showRankingDetailModal && selectedRankingTherapist && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-8 border w-full max-w-4xl shadow-2xl rounded-2xl bg-white my-10">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                Ranking Details: {selectedRankingTherapist.therapistInfo.name}
              </h2>
              <button
                onClick={() => {
                  setShowRankingDetailModal(false);
                  setSelectedRankingTherapist(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="max-h-[80vh] overflow-y-auto space-y-6">
              {/* Overview */}
              <div className="bg-[#e4fde1]/20 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-[#254e70] mb-4" style={{fontFamily: 'Kanit, sans-serif'}}>
                  Ranking Overview
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#254e70] mb-1">Rank</label>
                    <div className="text-3xl font-bold text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                      #{selectedRankingTherapist.rank}
                    </div>
                  </div>
                                     <div>
                     <label className="block text-sm font-medium text-[#254e70] mb-1">Total Score</label>
                     <div className="text-3xl font-bold text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                       {(selectedRankingTherapist.totalScore || 0).toFixed(1)}
                     </div>
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-[#254e70] mb-1">Base Score</label>
                     <div className="text-2xl font-bold text-gray-600" style={{fontFamily: 'Kanit, sans-serif'}}>
                       {(selectedRankingTherapist.baseScore || 0).toFixed(1)}
                     </div>
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-[#254e70] mb-1">New Client Multiplier</label>
                     <div className={`text-2xl font-bold ${selectedRankingTherapist.acceptingNewClients ? 'text-green-600' : 'text-red-600'}`} style={{fontFamily: 'Kanit, sans-serif'}}>
                       {(selectedRankingTherapist.acceptingNewClientsMultiplier || 0)}x
                     </div>
                   </div>
                </div>
              </div>

              {/* Proximity Breakdown */}
              <div className="bg-blue-50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-[#254e70] mb-4" style={{fontFamily: 'Kanit, sans-serif'}}>
                  Proximity Score (35% weight)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                     <div>
                     <label className="block text-sm font-medium text-[#254e70] mb-1">Raw Score</label>
                     <div className="text-2xl font-bold text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                       {(selectedRankingTherapist.categoryBreakdown?.proximity?.rawScore || 0).toFixed(1)}
                     </div>
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-[#254e70] mb-1">Weighted Score</label>
                     <div className="text-2xl font-bold text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                       {(selectedRankingTherapist.categoryBreakdown?.proximity?.weightedScore || 0).toFixed(1)}
                     </div>
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-[#254e70] mb-1">Distance</label>
                     <div className="text-lg font-medium text-gray-600" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                       {selectedRankingTherapist.categoryBreakdown?.proximity?.details?.distance ? 
                         `${selectedRankingTherapist.categoryBreakdown.proximity.details.distance.toFixed(1)} miles` : 
                         'Unknown'
                       }
                     </div>
                   </div>
                </div>
              </div>

              {/* Engagement Breakdown */}
              <div className="bg-green-50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-[#254e70] mb-4" style={{fontFamily: 'Kanit, sans-serif'}}>
                  Engagement Score (15% weight - Reduced to prevent feedback loop)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                     <div>
                     <label className="block text-sm font-medium text-[#254e70] mb-1">Raw Score</label>
                     <div className="text-2xl font-bold text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                       {(selectedRankingTherapist.categoryBreakdown?.engagement?.rawScore || 0).toFixed(1)}
                     </div>
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-[#254e70] mb-1">Weighted Score</label>
                     <div className="text-2xl font-bold text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                       {(selectedRankingTherapist.categoryBreakdown?.engagement?.weightedScore || 0).toFixed(1)}
                     </div>
                   </div>
                </div>
                <div className="mt-4">
                  <h4 className="font-semibold text-[#254e70] mb-2">Sub-Scores (with anti-feedback improvements):</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                         <div className="bg-white p-3 rounded-lg">
                       <label className="block text-xs font-medium text-[#254e70] mb-1">Search Click Rate (60%)</label>
                       <div className="text-lg font-bold text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                         {(selectedRankingTherapist.categoryBreakdown?.engagement?.subScores?.searchClickRate || 0).toFixed(1)}
                       </div>
                       <div className="text-xs text-gray-600 mt-1">
                         Rate: {selectedRankingTherapist.categoryBreakdown?.engagement?.details?.rawSearchClickRate?.toFixed(1) || 0}%
                       </div>
                     </div>
                     <div className="bg-white p-3 rounded-lg">
                       <label className="block text-xs font-medium text-[#254e70] mb-1">Contact Click Rate (40%)</label>
                       <div className="text-lg font-bold text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                         {(selectedRankingTherapist.categoryBreakdown?.engagement?.subScores?.contactClickRate || 0).toFixed(1)}
                       </div>
                       <div className="text-xs text-gray-600 mt-1">
                         Rate: {selectedRankingTherapist.categoryBreakdown?.engagement?.details?.rawContactClickRate?.toFixed(1) || 0}%
                       </div>
                     </div>
                  </div>
                  <div className="mt-4 bg-green-100 p-3 rounded-lg">
                    <p className="text-sm text-green-800" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                      <strong>Anti-feedback improvements:</strong> Minimum sample sizes prevent unreliable rates, 
                      rate caps prevent outliers, and new therapists get fair neutral scores instead of penalties.
                    </p>
                  </div>
                </div>
              </div>

              {/* Profile Quality Breakdown */}
              <div className="bg-purple-50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-[#254e70] mb-4" style={{fontFamily: 'Kanit, sans-serif'}}>
                  Profile Quality Score (40% weight - Increased for fairness)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                     <div>
                     <label className="block text-sm font-medium text-[#254e70] mb-1">Raw Score</label>
                     <div className="text-2xl font-bold text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                       {(selectedRankingTherapist.categoryBreakdown?.profileQuality?.rawScore || 0).toFixed(1)}
                     </div>
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-[#254e70] mb-1">Weighted Score</label>
                     <div className="text-2xl font-bold text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                       {(selectedRankingTherapist.categoryBreakdown?.profileQuality?.weightedScore || 0).toFixed(1)}
                     </div>
                   </div>
                </div>
                <div className="mt-4">
                  <h4 className="font-semibold text-[#254e70] mb-2">Sub-Scores:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                         <div className="bg-white p-3 rounded-lg">
                       <label className="block text-xs font-medium text-[#254e70] mb-1">Completeness (50%)</label>
                       <div className="text-lg font-bold text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                         {(selectedRankingTherapist.categoryBreakdown?.profileQuality?.subScores?.completeness || 0).toFixed(1)}
                       </div>
                     </div>
                     <div className="bg-white p-3 rounded-lg">
                       <label className="block text-xs font-medium text-[#254e70] mb-1">Welcome Video (25%)</label>
                       <div className="text-lg font-bold text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                         {(selectedRankingTherapist.categoryBreakdown?.profileQuality?.subScores?.welcomeVideo || 0).toFixed(1)}
                       </div>
                     </div>
                     <div className="bg-white p-3 rounded-lg">
                       <label className="block text-xs font-medium text-[#254e70] mb-1">Verification (15%)</label>
                       <div className="text-lg font-bold text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                         {(selectedRankingTherapist.categoryBreakdown?.profileQuality?.subScores?.verificationStatus || 0).toFixed(1)}
                       </div>
                     </div>
                     <div className="bg-white p-3 rounded-lg">
                       <label className="block text-xs font-medium text-[#254e70] mb-1">Photo Quality (10%)</label>
                       <div className="text-lg font-bold text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                         {(selectedRankingTherapist.categoryBreakdown?.profileQuality?.subScores?.photoQuality || 0).toFixed(1)}
                       </div>
                     </div>
                  </div>
                                     {(selectedRankingTherapist.categoryBreakdown?.profileQuality?.specializationBonus || 0) > 0 && (
                     <div className="mt-4 bg-yellow-50 p-3 rounded-lg">
                       <label className="block text-xs font-medium text-[#254e70] mb-1">Specialization Match Bonus</label>
                       <div className="text-lg font-bold text-yellow-600" style={{fontFamily: 'Kanit, sans-serif'}}>
                         +{(selectedRankingTherapist.categoryBreakdown?.profileQuality?.specializationBonus || 0).toFixed(1)}
                       </div>
                     </div>
                   )}
                </div>
              </div>

              {/* Randomness Breakdown */}
              <div className="bg-yellow-50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-[#254e70] mb-4" style={{fontFamily: 'Kanit, sans-serif'}}>
                  Randomness Score (10% weight - Prevents stagnation)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[#254e70] mb-1">Raw Score</label>
                    <div className="text-2xl font-bold text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                      {(selectedRankingTherapist.categoryBreakdown?.randomness?.rawScore || 0).toFixed(1)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#254e70] mb-1">Weighted Score</label>
                    <div className="text-2xl font-bold text-[#254e70]" style={{fontFamily: 'Kanit, sans-serif'}}>
                      {(selectedRankingTherapist.categoryBreakdown?.randomness?.weightedScore || 0).toFixed(1)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#254e70] mb-1">Time Window</label>
                    <div className="text-sm text-gray-600" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                      {selectedRankingTherapist.categoryBreakdown?.randomness?.details?.refreshesEvery || '6 hours'}
                    </div>
                  </div>
                </div>
                <div className="mt-4 bg-yellow-100 p-3 rounded-lg">
                  <p className="text-sm text-yellow-800" style={{fontFamily: 'Nunito Sans, sans-serif'}}>
                    <strong>How it works:</strong> This deterministic randomness changes every 6 hours to give different therapists exposure, 
                    preventing high-ranking therapists from staying at the top indefinitely. It uses the therapist ID + time window 
                    to ensure consistent but rotating results.
                  </p>
                </div>
              </div>

              {/* Therapist Info */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-[#254e70] mb-4" style={{fontFamily: 'Kanit, sans-serif'}}>
                  Therapist Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                     <div>
                     <label className="block text-sm font-medium text-[#254e70] mb-1">Location</label>
                     <p className="text-gray-900">{selectedRankingTherapist.therapistInfo?.city || 'N/A'}, {selectedRankingTherapist.therapistInfo?.state || 'N/A'}</p>
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-[#254e70] mb-1">Email</label>
                     <p className="text-gray-900">{selectedRankingTherapist.therapistInfo?.email || 'N/A'}</p>
                   </div>
                   <div className="md:col-span-2">
                     <label className="block text-sm font-medium text-[#254e70] mb-1">Specializations</label>
                     <p className="text-gray-900">
                       {selectedRankingTherapist.therapistInfo?.specializations?.length > 0 
                         ? selectedRankingTherapist.therapistInfo.specializations.join(', ') 
                         : 'Not specified'}
                     </p>
                   </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdminDashboard; 